import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, Form, Input, Modal, Popconfirm, Select, Space, Tag, Typography, message } from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import Table from '../../components/RecordTable';
import { labAPI, userAPI } from '../../services/api';
import { recordList } from '../../utils/records';

export default function LabAdmins() {
  const [admins, setAdmins] = useState([]), [labs, setLabs] = useState([]);
  const [page, setPage] = useState(1), [total, setTotal] = useState(0);
  const [search, setSearch] = useState(''), [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false), [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null), [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const request = useRef(0);
  const refresh = useCallback(async () => {
    const current = ++request.current;
    setLoading(true);
    try {
      const response = await userAPI.getAll({ role: 'labadmin', page, limit: 10, search, isActive: status });
      if (current !== request.current) return;
      const result = recordList(response, 'users');
      setAdmins(result.rows); setTotal(result.total);
    } catch (error) { if (current === request.current) message.error(error.message || 'Could not load lab admins'); }
    finally { if (current === request.current) setLoading(false); }
  }, [page, search, status]);
  useEffect(() => { refresh(); return () => { ++request.current; }; }, [refresh]);
  useEffect(() => {
    let active = true;
    (async () => {
      const all = [];
      let labPage = 1, pages = 1;
      do {
        const response = await labAPI.getAll({ page: labPage, limit: 100 });
        all.push(...recordList(response, 'labs').rows);
        pages = response.data?.pagination?.pages || 1;
        labPage++;
      } while (labPage <= pages);
      if (active) setLabs(all);
    })().catch(() => { if (active) message.error('Could not load laboratories. Reload this page to retry.'); });
    return () => { active = false; };
  }, []);
  function edit(admin = null) {
    setEditing(admin); form.resetFields();
    if (admin) form.setFieldsValue({ name: admin.name, email: admin.email, phone: admin.phone, labId: admin.labId?._id || admin.labId });
    setOpen(true);
  }
  async function save(values) {
    setSaving(true);
    try {
      const data = { name: values.name.trim(), email: values.email.trim(), phone: values.phone.trim(), labId: values.labId || null };
      if (values.password) data.password = values.password;
      if (editing) await userAPI.update(editing._id, data);
      else await userAPI.createUserByAdmin({ ...data, role: 'labadmin' });
      message.success(editing ? 'Lab admin updated' : 'Lab admin created');
      setOpen(false); await refresh();
    } catch (error) { message.error(error.response?.data?.message || error.message || 'Could not save lab admin'); }
    finally { setSaving(false); }
  }
  async function setActive(admin) {
    setSaving(true);
    try {
      await userAPI.update(admin._id, { isActive: !admin.isActive });
      message.success(admin.isActive ? 'Lab-admin access deactivated' : 'Lab-admin access restored');
      await refresh();
    } catch (error) { message.error(error.response?.data?.message || 'Could not change access'); }
    finally { setSaving(false); }
  }
  const columns = [
    { title: 'Administrator', key: 'name', render: (_, admin) => <><strong>{admin.name}</strong><div>{admin.email}</div><div>{admin.phone}</div></> },
    { title: 'Laboratory', key: 'lab', render: (_, admin) => admin.labId?.name || labs.find(lab => lab._id === admin.labId)?.name || 'Unassigned' },
    { title: 'Access', key: 'active', render: (_, admin) => <Tag color={admin.isActive ? 'green' : 'default'}>{admin.isActive ? 'Active' : 'Inactive'}</Tag> },
    { title: 'Actions', key: 'actions', render: (_, admin) => <Space wrap>
      <Button onClick={() => edit(admin)}>Edit</Button>
      {admin.isActive && <Link to={`/lab/${admin._id}`}>Open workspace</Link>}
      <Popconfirm title={admin.isActive ? 'Deactivate lab-admin access?' : 'Restore lab-admin access?'} description="Existing bookings and laboratory records are kept." onConfirm={() => setActive(admin)}>
        <Button danger={admin.isActive} disabled={saving}>{admin.isActive ? 'Deactivate' : 'Reactivate'}</Button>
      </Popconfirm>
    </Space> },
  ];
  return <>
    <Space wrap style={{ width: '100%', justifyContent: 'space-between', marginBottom: 24 }}>
      <div><Typography.Title level={2}>Lab admins</Typography.Title><Typography.Text type="secondary">Manage laboratory access and assignments.</Typography.Text></div>
      <Space><Button icon={<ReloadOutlined />} onClick={refresh} loading={loading}>Refresh</Button><Button type="primary" icon={<PlusOutlined />} onClick={() => edit()}>Add lab admin</Button></Space>
    </Space>
    <Card>
      <Space wrap style={{ marginBottom: 20 }}>
        <Input.Search aria-label="Search lab admins" placeholder="Name, email or phone" allowClear onSearch={value => { setPage(1); setSearch(value.trim()); }} />
        <Select aria-label="Lab-admin access" value={status} onChange={value => { setPage(1); setStatus(value); }} style={{ minWidth: 150 }} options={[{ value: '', label: 'All access' }, { value: 'true', label: 'Active' }, { value: 'false', label: 'Inactive' }]} />
      </Space>
      <Table rowKey="_id" columns={columns} dataSource={admins} loading={loading} pagination={{ current: page, pageSize: 10, total, hideOnSinglePage: true, showSizeChanger: false, onChange: setPage }} />
    </Card>
    <Modal title={editing ? 'Edit lab admin' : 'Add lab admin'} open={open} onCancel={() => !saving && setOpen(false)} footer={null}>
      <Form form={form} layout="vertical" onFinish={save}>
        <Form.Item name="name" label="Name" rules={[{ required: true, whitespace: true }]}><Input /></Form.Item>
        <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}><Input type="email" /></Form.Item>
        <Form.Item name="phone" label="Phone" rules={[{ required: true, whitespace: true }]}><Input type="tel" /></Form.Item>
        <Form.Item name="labId" label="Laboratory"><Select allowClear showSearch optionFilterProp="label" placeholder="Unassigned" options={labs.map(lab => ({ value: lab._id, label: lab.name }))} /></Form.Item>
        <Form.Item name="password" label={editing ? 'New password (optional)' : 'Password'} rules={[{ required: !editing }, { min: 8 }]}><Input.Password autoComplete="new-password" /></Form.Item>
        <Button type="primary" htmlType="submit" loading={saving}>Save lab admin</Button>
      </Form>
    </Modal>
  </>;
}
